import secureApiService from './secureApiService'
import {
  ProgrammeCurriculumJSON,
  ProgrammeCurriculumModel,
} from '@/models/programme-curriculum.model'

export interface ProgrammeCurriculumMutationPayload {
  courseId: string
  year?: number | null
  semester?: number | null
  required?: boolean | null
  order?: number | null
}

interface ProgrammeCurriculumCollectionResponseJSON {
  success: boolean
  message?: string
  data?: ProgrammeCurriculumJSON[] | ProgrammeCurriculumJSON | null
}

interface ProgrammeCurriculumSingleResponseJSON {
  success: boolean
  message?: string
  data?: ProgrammeCurriculumJSON | null
}

class ProgrammeCurriculumService {
  private getBasePath(programmeId: string) {
    return `/programmes/${programmeId}/curriculum`
  }

  async getCurriculums(programmeId: string): Promise<ProgrammeCurriculumModel[]> {
    const response = await secureApiService.get<ProgrammeCurriculumCollectionResponseJSON>(
      this.getBasePath(programmeId),
      { requireAuth: true },
    )

    const payload = response.data

    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch programme curriculums')
    }

    const rawData = Array.isArray(payload.data)
      ? payload.data
      : payload.data
        ? [payload.data]
        : []

    return rawData
      .filter((item): item is ProgrammeCurriculumJSON => Boolean(item))
      .map((item) => ProgrammeCurriculumModel.fromJSON(item))
  }

  async getCurriculum(programmeId: string, curriculumId: string): Promise<ProgrammeCurriculumModel> {
    const response = await secureApiService.get<ProgrammeCurriculumSingleResponseJSON>(
      `${this.getBasePath(programmeId)}/${curriculumId}`,
      { requireAuth: true },
    )

    const payload = response.data
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Unable to fetch programme curriculum')
    }

    return ProgrammeCurriculumModel.fromJSON(payload.data)
  }

  async createCurriculum(
    programmeId: string,
    payload: ProgrammeCurriculumMutationPayload,
  ): Promise<ProgrammeCurriculumModel> {
    const response = await secureApiService.post<ProgrammeCurriculumSingleResponseJSON>(
      this.getBasePath(programmeId),
      {
        courseId: payload.courseId,
        year: payload.year ?? null,
        semester: payload.semester ?? null,
        required: payload.required ?? false,
        order: payload.order ?? null,
      },
    )

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to create programme curriculum')
    }

    return ProgrammeCurriculumModel.fromJSON(body.data)
  }

  async updateCurriculum(
    programmeId: string,
    curriculumId: string,
    payload: ProgrammeCurriculumMutationPayload,
  ): Promise<ProgrammeCurriculumModel> {
    const response = await secureApiService.put<ProgrammeCurriculumSingleResponseJSON>(
      `${this.getBasePath(programmeId)}/${curriculumId}`,
      {
        courseId: payload.courseId,
        year: payload.year ?? null,
        semester: payload.semester ?? null,
        required: payload.required ?? false,
        order: payload.order ?? null,
      },
    )

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to update programme curriculum')
    }

    return ProgrammeCurriculumModel.fromJSON(body.data)
  }

  async deleteCurriculum(programmeId: string, curriculumId: string): Promise<void> {
    const response = await secureApiService.delete<ProgrammeCurriculumSingleResponseJSON>(
      `${this.getBasePath(programmeId)}/${curriculumId}`,
      { requireAuth: true },
    )

    const body = response.data
    if (!body?.success) {
      throw new Error(body?.message || 'Unable to delete programme curriculum')
    }
  }
}

export const programmeCurriculumService = new ProgrammeCurriculumService()
